import { useState, useMemo, useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPencil,
  faTrash,
  faFileArrowDown,
  faCalendarDays,
  faLocationDot,
  faUsers,
  faSpinner,
  faWandMagicSparkles,
  faCloudArrowDown,
  faComments,
  faRotate,
} from '@fortawesome/free-solid-svg-icons';
import { faGoogleDrive } from '@fortawesome/free-brands-svg-icons';
import { useGoogleLogin } from '@react-oauth/google';
import toast from 'react-hot-toast';

import { api } from '@/lib/axios';
import { API_ROUTES, ROUTES } from '@/constants';
import { Button, ImageLightbox, Spinner } from '@/components/ui';
import { parseDeck } from '@/lib/presentation';
import { useDriveUpload, useDrivePull, useThemes } from '@/api/usePresentationWizard';
import { PresentationEditor } from '@/components/PresentationEditor/PresentationEditor';
import { migrateV1toV2, isDeckV2 } from '@/lib/migrateSlideSchema';
import { PresentationChat } from '@/components/PresentationChat/PresentationChat';
import { GenerationProgress } from '@/components/GenerationProgress';
import type { PresentationItem } from '@/types/presentation';
import type { DeckV2 } from '@/types/slide';
import styles from './PresentationDetail.module.css';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  generating: 'Generating...',
  completed: 'Completed',
  failed: 'Failed',
};

const CONTEXT_LABELS: Record<string, string> = {
  event_name: 'Event',
  client_name: 'Client',
  event_date: 'Date',
  venue: 'Venue',
  attendees: 'Attendees',
  theme_slugs: 'Themes',
  plantilla_slug: 'Template',
};

type LayoutContext = {
  setHeaderSlot: (node: ReactNode) => void;
  setHeaderTitle: (node: ReactNode) => void;
};

export function PresentationDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { setHeaderSlot, setHeaderTitle } = useOutletContext<LayoutContext>();

  const [currentSlide] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [showChat, setShowChat] = useState(false);

  const { data: item, isLoading } = useQuery({
    queryKey: ['presentation-detail', id],
    queryFn: async () => {
      const res = await api.get<PresentationItem>(`${API_ROUTES.presentations}${id}/`);
      return res.data;
    },
    enabled: Boolean(id),
    refetchInterval: (query) => (query.state.data?.status === 'generating' ? 3000 : false),
  });

  const handleDeckUpdate = useCallback(
    (newDeck: DeckV2) => {
      queryClient.setQueryData(['presentation-detail', id], (old: PresentationItem | undefined) => {
        if (!old) return old;
        return { ...old, deck_json: newDeck };
      });
    },
    [id, queryClient],
  );

  const driveUpload = useDriveUpload();
  const drivePull = useDrivePull();

  const deckV2 = useMemo(() => {
    if (!item?.deck_json) return null;
    const raw =
      typeof item.deck_json === 'string'
        ? JSON.parse(item.deck_json as unknown as string)
        : item.deck_json;
    if (!raw || typeof raw !== 'object' || !('slides' in raw)) return null;
    return isDeckV2(raw) ? raw : migrateV1toV2(raw);
  }, [item?.deck_json]);

  // Fallback OAuth popup — only triggered when the backend reports no stored token
  const googleLoginForUpload = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      if (!id) return;
      toast.loading('Uploading to Google Drive...', { id: 'drive-upload' });
      driveUpload.mutate(
        { presentationId: id, access_token: tokenResponse.access_token },
        {
          onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['presentation-detail', id] });
            window.open(data.drive_url, '_blank');
            toast.success('Uploaded to Google Drive', { id: 'drive-upload' });
          },
          onError: (err) => {
            const msg =
              (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
              'Upload failed';
            toast.error(msg, { id: 'drive-upload' });
          },
        },
      );
    },
    onError: () => {
      toast.dismiss('drive-upload');
      toast.error('Google sign-in was cancelled');
    },
    scope: 'https://www.googleapis.com/auth/drive.file',
  });

  const googleLoginForPull = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      if (!id) return;
      toast.loading('Syncing from Google Drive...', { id: 'drive-pull' });
      drivePull.mutate(
        { presentationId: id, access_token: tokenResponse.access_token },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['presentation-detail', id] });
            toast.success('Synced from Google Drive', { id: 'drive-pull' });
          },
          onError: (err) => {
            const msg =
              (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
              'Sync failed';
            toast.error(msg, { id: 'drive-pull' });
          },
        },
      );
    },
    onError: () => {
      toast.dismiss('drive-pull');
      toast.error('Google sign-in was cancelled');
    },
    scope: 'https://www.googleapis.com/auth/drive.file',
  });

  const handleEditOnDrive = () => {
    if (!id) return;

    // Current user already has their own copy → just open it
    if (item?.my_drive_url) {
      window.open(item.my_drive_url, '_blank');
      return;
    }

    // Try stored token first; backend falls back to popup only if 400 "not connected"
    toast.loading('Uploading to Google Drive...', { id: 'drive-upload' });
    driveUpload.mutate(
      { presentationId: id },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries({ queryKey: ['presentation-detail', id] });
          window.open(data.drive_url, '_blank');
          toast.success('Uploaded to Google Drive', { id: 'drive-upload' });
        },
        onError: (err) => {
          const axErr = err as { response?: { status?: number; data?: { error?: string } } };
          const msg = axErr?.response?.data?.error ?? '';
          if (axErr?.response?.status === 400 && msg.toLowerCase().includes('not connected')) {
            toast.dismiss('drive-upload');
            googleLoginForUpload();
          } else {
            toast.error(msg || 'Upload failed', { id: 'drive-upload', duration: 8000 });
          }
        },
      },
    );
  };

  const handleSyncFromDrive = () => {
    if (!id) return;

    // Try stored token first; backend falls back to popup only if 400 "not connected"
    toast.loading('Syncing from Google Drive...', { id: 'drive-pull' });
    drivePull.mutate(
      { presentationId: id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['presentation-detail', id] });
          toast.success('Synced from Google Drive', { id: 'drive-pull' });
        },
        onError: (err) => {
          const axErr = err as { response?: { status?: number; data?: { error?: string } } };
          const msg = axErr?.response?.data?.error ?? '';
          if (axErr?.response?.status === 400 && msg.toLowerCase().includes('not connected')) {
            toast.dismiss('drive-pull');
            googleLoginForPull();
          } else {
            toast.error(msg || 'Sync failed', { id: 'drive-pull', duration: 8000 });
          }
        },
      },
    );
  };

  const regenerateMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`${API_ROUTES.presentations}${id}/generate/`, {
        regenerate: true,
        regenerate_assets: true,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['presentation-detail', id] });
      toast.success('Regeneration started');
    },
    onError: () => {
      toast.error('Failed to start regeneration');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`${API_ROUTES.presentations}${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['presentations'] });
      navigate(ROUTES.dashboardPresentations);
    },
  });

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (!item?.pptx_url) return;
    try {
      setIsDownloading(true);
      const response = await fetch(item.pptx_url);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const safeName = (item.name || 'presentation').replace(/[\\/:*?"<>|]/g, '_').trim() || 'presentation';
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${safeName}.pptx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      toast.success('Download started');
    } catch (err) {
      toast.error('Could not download the file');
      console.error(err);
    } finally {
      setIsDownloading(false);
    }
  };

  const editPath = item?.id
    ? ROUTES.dashboardPresentationEdit.replace(':id', String(item.id))
    : '';

  // ── Inject the unified header into DashboardLayout ──
  // All the action buttons that used to live in the local action bar now
  // live in the app header's slot, matching the edit page's pattern.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!item) {
      setHeaderTitle(null);
      setHeaderSlot(null);
      return;
    }

    setHeaderTitle(
      <span className={styles.headerTitleText}>
        <span className={styles.headerTitleName}>{item.name}</span>
        <span
          className={`${styles.headerStatusBadge} ${styles[`status_${item.status}`]}`}
        >
          {item.status === 'generating' && (
            <FontAwesomeIcon icon={faSpinner} className={styles.statusSpinnerIcon} />
          )}
          {STATUS_LABELS[item.status] ?? item.status}
        </span>
      </span>,
    );

    const canRegenerate = item.status === 'draft' || item.status === 'failed';
    const canEditInDrive = item.status === 'completed' && Boolean(item.pptx_url);

    setHeaderSlot(
      <>
        <button
          type="button"
          className={styles.headerBtn}
          onClick={() => navigate(ROUTES.dashboardPresentations)}
          title="Back to presentations"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          <span className={styles.headerBtnLabel}>Back</span>
        </button>

        {canRegenerate && (
          <button
            type="button"
            className={`${styles.headerBtn} ${styles.headerBtnPrimary}`}
            onClick={() => regenerateMutation.mutate()}
            disabled={regenerateMutation.isPending}
            title="Regenerate"
          >
            <FontAwesomeIcon
              icon={regenerateMutation.isPending ? faSpinner : faRotate}
            />
            <span className={styles.headerBtnLabel}>
              {regenerateMutation.isPending ? 'Starting…' : 'Regenerate'}
            </span>
          </button>
        )}

        <button
          type="button"
          className={styles.headerBtn}
          onClick={() => navigate(editPath)}
          title="Edit"
        >
          <FontAwesomeIcon icon={faPencil} />
          <span className={styles.headerBtnLabel}>Edit</span>
        </button>

        {item.pptx_url && (
          <button
            type="button"
            className={styles.headerBtn}
            onClick={() => void handleDownload()}
            disabled={isDownloading}
            title="Download PPTX"
          >
            <FontAwesomeIcon icon={isDownloading ? faSpinner : faFileArrowDown} />
            <span className={styles.headerBtnLabel}>
              {isDownloading ? 'Downloading…' : 'Download'}
            </span>
          </button>
        )}

        {canEditInDrive && (
          <button
            type="button"
            className={`${styles.headerBtn} ${styles.headerBtnPrimary}`}
            onClick={handleEditOnDrive}
            disabled={driveUpload.isPending}
            title={item.my_drive_url ? 'Open in Google Drive' : 'Edit in Google Drive'}
          >
            <FontAwesomeIcon
              icon={driveUpload.isPending ? faSpinner : faGoogleDrive}
            />
            <span className={styles.headerBtnLabel}>
              {driveUpload.isPending
                ? 'Uploading…'
                : item.my_drive_url
                  ? 'Drive'
                  : 'Drive'}
            </span>
          </button>
        )}

        {item.my_drive_file_id && (
          <button
            type="button"
            className={styles.headerBtn}
            onClick={handleSyncFromDrive}
            disabled={drivePull.isPending}
            title="Sync from Drive"
          >
            <FontAwesomeIcon
              icon={drivePull.isPending ? faSpinner : faCloudArrowDown}
            />
            <span className={styles.headerBtnLabel}>
              {drivePull.isPending ? 'Syncing…' : 'Sync'}
            </span>
          </button>
        )}

        <button
          type="button"
          className={`${styles.headerBtn} ${styles.headerBtnDanger}`}
          onClick={() => {
            if (window.confirm('Delete this presentation?')) {
              deleteMutation.mutate();
            }
          }}
          disabled={deleteMutation.isPending}
          title="Delete"
        >
          <FontAwesomeIcon icon={faTrash} />
          <span className={styles.headerBtnLabel}>Delete</span>
        </button>
      </>,
    );

    return () => {
      setHeaderTitle(null);
      setHeaderSlot(null);
    };
  }, [
    item?.name,
    item?.status,
    item?.pptx_url,
    item?.pptx_code,
    item?.my_drive_url,
    item?.my_drive_file_id,
    editPath,
    isDownloading,
    regenerateMutation.isPending,
    driveUpload.isPending,
    drivePull.isPending,
    deleteMutation.isPending,
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Resolve theme slug(s) to human names via the themes registry so the
  // metadata strip shows "Corporativo" instead of "corporativo". Must be
  // called before any early return to keep hook order stable.
  const themesQuery = useThemes();

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner />
      </div>
    );
  }

  if (!item) return null;

  const deck = parseDeck(item.pptx_code);

  const heroImage = item.generated_images?.[0];
  const primaryColor = deck.theme?.primary_color ?? '#0C1B33';
  const accentColor = deck.theme?.accent_color ?? '#C9A84C';

  const ctx = item.context_data ?? {};
  const contextFields = Object.entries(CONTEXT_LABELS).filter(
    ([key]) => ctx[key] != null && String(ctx[key]).trim() !== '',
  );

  const hasHeroChips =
    Boolean(ctx.event_date) || Boolean(ctx.venue) || Boolean(ctx.attendees);

  const themeNames: string[] = (item.theme_slugs ?? []).map((slug) => {
    const t = themesQuery.data?.find((x) => x.slug === slug);
    return t?.name ?? slug;
  });

  // Seed prompt the user originally wrote in the wizard — persisted by
  // wizard_generate into context_data.prompt. Showing it lets the user
  // remember what they asked for, which is especially handy for
  // failed / stale rows where they're deciding whether to retry.
  const seedPrompt =
    typeof ctx.prompt === 'string' && ctx.prompt.trim() !== ''
      ? String(ctx.prompt).trim()
      : '';

  // Failure error message written by tasks.py into
  // generation_progress.error on any terminal exception. Surfaces the
  // real reason in the failed banner so the user doesn't have to guess.
  const failureError =
    typeof item.generation_progress?.error === 'string'
      ? item.generation_progress.error
      : '';

  return (
    <div className={styles.page}>
      {/* ── Compact visual banner (no title — that lives in the app header now) ── */}
      {hasHeroChips && (
        <div
          className={styles.hero}
          style={
            heroImage
              ? { backgroundImage: `url(${heroImage})` }
              : {
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
                }
          }
        >
          <div className={styles.heroOverlay} />
          <div className={styles.heroContent}>
            <div className={styles.heroChips}>
              {Boolean(ctx.event_date) && (
                <span className={styles.chip}>
                  <FontAwesomeIcon icon={faCalendarDays} />
                  {String(ctx.event_date)}
                </span>
              )}
              {Boolean(ctx.venue) && (
                <span className={styles.chip}>
                  <FontAwesomeIcon icon={faLocationDot} />
                  {String(ctx.venue)}
                </span>
              )}
              {Boolean(ctx.attendees) && (
                <span className={styles.chip}>
                  <FontAwesomeIcon icon={faUsers} />
                  {String(ctx.attendees)} attendees
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Failed banner ────────────────────────────────────── */}
      {item.status === 'failed' && (
        <div className={styles.failedBanner}>
          <div className={styles.failedBannerInner}>
            <p className={styles.failedTitle}>Generation failed</p>
            <p className={styles.failedSubtitle}>
              {failureError || 'Something went wrong during generation. You can try again.'}
            </p>
            <Button
              variant="primary"
              size="sm"
              onClick={() => regenerateMutation.mutate()}
              disabled={regenerateMutation.isPending}
            >
              <FontAwesomeIcon icon={regenerateMutation.isPending ? faSpinner : faRotate} />
              {regenerateMutation.isPending ? 'Starting...' : 'Retry Generation'}
            </Button>
          </div>
        </div>
      )}

      {/* ── Generating banner ──────────────────────────────────── */}
      {item.status === 'generating' && (
        <div className={styles.generatingBanner}>
          <div className={styles.generatingBannerInner}>
            {item.generation_progress ? (
              <GenerationProgress progress={item.generation_progress} mode="detailed" />
            ) : (
              <>
                <div className={styles.generatingPulse} />
                <div>
                  <p className={styles.generatingTitle}>
                    <FontAwesomeIcon icon={faWandMagicSparkles} />
                    Generating presentation...
                  </p>
                  <p className={styles.generatingSubtitle}>
                    The page will update automatically when slides are ready.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className={styles.body}>
        {/* ── 3. PPTX Viewer (ONLYOFFICE read-only) ──────── */}
        {item.status === 'completed' && item.pptx_url && (
          <div className={styles.viewerSection}>
            <div className={styles.viewerContainer}>
              <div className={styles.viewerMain}>
                <PresentationEditor presentation={item} mode="view" />
              </div>
              {showChat && id && deckV2 && (
                <div className={styles.chatPanel}>
                  <PresentationChat
                    presentationId={id}
                    currentSlideIndex={currentSlide}
                    currentSlide={deckV2.slides[currentSlide] ?? null}
                    onDeckUpdate={handleDeckUpdate}
                  />
                </div>
              )}
            </div>
            <div className={styles.viewerActions}>
              <button onClick={() => setShowChat((s) => !s)} className={styles.slideNavChat}>
                <FontAwesomeIcon icon={faComments} />
                {showChat ? 'Close Chat' : 'AI Chat'}
              </button>
            </div>
          </div>
        )}

        {/* ── Images strip (slides + generated images unified) ─ */}
        {(() => {
          const generated = item.generated_images ?? [];
          const slideImages = deck.slides
            .map((s) => s.image_url)
            .filter((u): u is string => Boolean(u));
          const galleryImages = Array.from(new Set([...slideImages, ...generated]));
          if (galleryImages.length === 0) return null;
          return (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>
                Images
                <span className={styles.sectionCount}>{galleryImages.length}</span>
              </h2>
              <div className={styles.slidesStrip}>
                {galleryImages.map((url, idx) => (
                  <button
                    key={url}
                    type="button"
                    className={styles.slideCard}
                    onClick={() => setLightboxIndex(idx)}
                    aria-label={`Open image ${idx + 1}`}
                  >
                    <img src={url} alt="" className={styles.slideCardBg} />
                    <div className={styles.slideCardOverlay} />
                    <span className={styles.slideNum}>{idx + 1}</span>
                  </button>
                ))}
              </div>
              <ImageLightbox
                images={galleryImages}
                openIndex={lightboxIndex}
                onClose={() => setLightboxIndex(null)}
                downloadFilename={`${item.name || 'presentation'}-image`}
              />
            </section>
          );
        })()}

        {/* ── 7. Context info card ──────────────────────────── */}
        {(contextFields.length > 0 || themeNames.length > 0 || seedPrompt || deck.theme) && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Event Information</h2>
            <div className={styles.contextCard}>
              {deck.theme && (
                <div className={styles.themeSwatches}>
                  <div
                    className={styles.swatch}
                    style={{ background: deck.theme.primary_color }}
                    title={`Primary: ${deck.theme.primary_color}`}
                  />
                  <div
                    className={styles.swatch}
                    style={{ background: deck.theme.accent_color }}
                    title={`Accent: ${deck.theme.accent_color}`}
                  />
                  <span className={styles.swatchFonts}>
                    {deck.theme.font_title} / {deck.theme.font_body}
                  </span>
                </div>
              )}
              {contextFields.length > 0 && (
                <dl className={styles.contextGrid}>
                  {contextFields.map(([key, label]) => (
                    <div key={key} className={styles.contextRow}>
                      <dt className={styles.contextKey}>{label}</dt>
                      <dd className={styles.contextVal}>{String(ctx[key])}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {themeNames.length > 0 && (
                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>Theme</span>
                  <div className={styles.metaChips}>
                    {themeNames.map((name) => (
                      <span key={name} className={styles.metaChip}>
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {seedPrompt && (
                <div className={styles.metaRow}>
                  <span className={styles.metaLabel}>Seed prompt</span>
                  <p className={styles.metaPrompt}>{seedPrompt}</p>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
